import type { CompanyInvitation } from "@repo/app-types/entities/companyInvitation";
import type { TeammateLicenseType } from "@repo/app-types/entities/teammate";
import type { TeammatePermissions } from "@repo/app-types/entities/teammatePermissions";
import {
  buildListTag,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

interface InviteTeammatesRequest {
  invites: {
    email: string;
    permissions: TeammatePermissions;
    license_type?: TeammateLicenseType;
  }[];
}

interface InviteTeammatesResult {
  company_invitations: CompanyInvitation[];
  invalid_emails: unknown[];
  /**
   * Addresses already belonging to an active teammate who has signed in.
   * Optional while an older API version may still be serving during a rollout.
   */
  skipped_emails?: string[];
  /**
   * Addresses belonging to a deactivated teammate. An invitation never
   * reactivates anybody, so they are not invited at all — an admin reactivates
   * them from the teammates list. Optional while an older API version may still
   * be serving during a rollout.
   */
  deactivated_emails?: string[];
}

const companyInvitationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    deleteCompanyInvitation: builder.mutation<void, string>({
      query: (id) => ({
        method: "DELETE",
        url: `/company-invitations/${encodeURIComponent(id)}`,
      }),
      // Cancelling drops the teammate row's "Invited" state as well, and both
      // lists are cached per search term (the roster per deactivated filter
      // too), so the entries the user isn't looking at have to go too.
      invalidatesTags: () => [
        buildListTag(TagType.CompanyInvitation, []),
        buildListTag(TagType.Teammate, []),
      ],
    }),

    inviteTeammates: builder.mutation<
      InviteTeammatesResult,
      InviteTeammatesRequest,
      { data: InviteTeammatesResult }
    >({
      query: (body) => ({
        method: "POST",
        url: "/company-invitations/invite",
        body,
      }),
      transformResponse: (response) => response.data,
      // An invitation changes how an existing teammate is listed (their row
      // turns into "Invited"), and can revive a deactivated one.
      invalidatesTags: () => [
        buildListTag(TagType.CompanyInvitation, []),
        buildListTag(TagType.Teammate, []),
      ],
    }),

    pendingInvitationsPaginated: builder.infiniteQuery<
      {
        invitations: CompanyInvitation[];
        nextCursor: string | null;
      },
      { search?: string },
      string | null,
      {
        data: { invitations: CompanyInvitation[] };
        next_cursor: string | null;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        method: "GET",
        url: "/company-invitations/pending-invitations/paginated",
        params: {
          cursor: pageParam ?? undefined,
          limit: 20,
          search: queryArg.search ?? undefined,
        },
      }),
      transformResponse: (response) => ({
        invitations: response.data.invitations,
        nextCursor: response.next_cursor,
      }),
      // `search` is part of the cache key, so every term is its own entry. They
      // all provide the same list tag so a mutation invalidates the entries the
      // user isn't looking at too.
      providesTags: (result) =>
        getTagsProvidedByList(
          TagType.CompanyInvitation,
          result?.pages.flatMap((page) => page.invitations),
        ),
    }),
  }),
});

export const {
  useDeleteCompanyInvitationMutation,
  useInviteTeammatesMutation,
  usePendingInvitationsPaginatedInfiniteQuery,
} = companyInvitationApi;
